import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";
import {Formik} from "formik";
import {RoleNameEnum} from "../../../../utils/types/common";
import {
  Template,
  getValidationConfig,
  extractTemplateSlides,
} from "@reside/forms";
import {
  BlockField,
  QueryVariableMap,
  BlockComponentContextProvider,
  FormikValidateAllOnSubmit,
  FormikAutoSaveValues,
  ScrollToError,
} from "@reside/ui";

import {transComponents} from "../../../../atoms/block-components";

import {
  FacilitySettingsVariables,
  FieldArrayItem,
  WorkflowSettingsVariables,
} from "../../../../services/FacilitySettingsService";
import {FormikReinitializePcc} from "../../hooks/useFormikReinitializePcc";
import {FacilityPccMappingForm} from "./forms/FacilityPccMappingForm";
import {
  EditFacilityConfirmationModal,
  EditFacilityModalProvider,
} from "../../../../atoms/edit-facility-confirmation-modal";
import {
  StyledForm,
  StyledScrollableErrorArea,
  PageLayout,
  ContentPane,
  ScrollableContent,
  ReadOnlyFieldset,
} from "./styles";
import {getChecklistWithNormalizedIds} from "../../utils";
import {
  FACILITY_GO_LIVE_DATE_BLOCK,
  INCLUDE_IN_AUDIT_LOG_BLOCK,
} from "./consts";
import {withAccessToken} from "../../../../utils/url";
import {getCommonAuthorizationHeader} from "../../../../services/api/selectors";
import {ScrollSpyProvider} from "../../../../hooks/useScrollSpy";
import {useFacilityWorkflowsQuery} from "../../../../atoms/start-workflow/hooks/useFacilityWorkflowsQuery";
import {FacilitySettingsSideNav} from "./FacilitySettingsSideNav";
import {
  disableBlocks,
  partitionFacilitySettingsBlocks,
} from "./facilitySettingsBlocks";
import {FacilitySettingsReadOnlyProvider} from "./FacilitySettingsReadOnlyContext";
import {WorkflowSettingsSection} from "./WorkflowSettingsSection";
import {
  validateWorkflowChecklistDescriptions,
  WorkflowSettingsValidator,
} from "./workflowSettingsBlocks";
import {WorkflowTriggersSection} from "../workflow-triggers-section";
import {ChangeTemplateModal} from "./ChangeTemplateModal";
import {useFacilitySettingsParams} from "../../hooks/useFacilitySettingsParams";
import {
  FacilitySettingsSection,
  FacilitySettingsScrollTarget,
  FACILITY_SETTINGS_SCROLL_CONTAINER_CLASS,
  SECTION_GENERAL,
} from "./FacilitySettingsSection";
import {history, type Transition} from "../../../../utils/history";

function ConfirmBeforeLeave({
  when,
  message,
}: {
  when: boolean;
  message: string;
}): null {
  useEffect(() => {
    if (!when) {
      return;
    }

    const unblock = history.block((tx: Transition) => {
      if (window.confirm(message)) {
        unblock();
        tx.retry();
      }
    });

    return unblock;
  }, [when, message]);

  return null;
}

export type Props = Readonly<{
  header: ReactNode;
  template: Template;
  queryVariables?: QueryVariableMap;
  /**
   * Initial Form values for the template.
   */
  initialValues: FacilitySettingsVariables;
  isPccEnabled?: boolean;
  /**
   * Inactive facilities can't be edited.
   */
  isReadOnly?: boolean;
  onSubmit?: (
    values: FacilitySettingsVariables,
  ) => Promise<FacilitySettingsVariables>;
}>;

export const FacilitySettingsForm = ({
  header,
  template,
  initialValues,
  onSubmit,
  queryVariables,
  isPccEnabled,
  isReadOnly = false,
}: Props) => {
  const [values, setValues] =
    useState<FacilitySettingsVariables>(initialValues);

  const [isChangeTemplateModalOpen, setIsChangeTemplateModalOpen] =
    useState(false);
  const {facilityId} = useFacilitySettingsParams();
  const {data: facilityWorkflows} = useFacilityWorkflowsQuery(facilityId);
  const workflows = facilityWorkflows?.workflows ?? [];
  const isResideAdmin = initialValues.userRole === RoleNameEnum.RESIDE_ADMIN;

  const [{children: blocks}] = useMemo(() => {
    const extractedBlocks = extractTemplateSlides(template, values as any);

    //@ts-expect-error - children exist
    extractedBlocks[0]?.children[0]?.children.splice(2, 0, {
      ...FACILITY_GO_LIVE_DATE_BLOCK,
      state: {
        ...FACILITY_GO_LIVE_DATE_BLOCK.state,
        disabled: initialValues.userRole !== RoleNameEnum.RESIDE_ADMIN,
      },
    }); // Inject go live date block after facility name block manually because it is not part of the template.

    if (initialValues.userRole === RoleNameEnum.RESIDE_ADMIN) {
      const enrich = (node: any): boolean => {
        if (!node) return false;
        if (node.id === "checklist" && Array.isArray(node.children)) {
          node.children = [
            ...node.children,
            INCLUDE_IN_AUDIT_LOG_BLOCK,
          ] as typeof node.children;
          return true;
        }
        const kids = [...(Array.isArray(node.children) ? node.children : [])];
        for (const k of kids) if (enrich(k)) return true;
        return false;
      };

      for (const block of extractedBlocks) {
        if (enrich(block)) break;
      }
    }

    return isReadOnly ? disableBlocks(extractedBlocks) : extractedBlocks;
  }, [values, template, initialValues.userRole, isReadOnly]);

  const {generalBlocks, pccTemplateBlocks} = useMemo(
    () => partitionFacilitySettingsBlocks(blocks),
    [blocks],
  );

  const {validate, validationRules} = useMemo(
    () =>
      getValidationConfig({
        children: blocks,
        answers: values,
      }),
    [blocks, values],
  );

  // TODO(RESIDE-3488): Support relative rules instead of this
  const estimatedMaxCostForSNFABN = (
    (validationRules.estimatedMaxCostForSNFABN || []) as ReadonlyArray<string>
  ).map(rule =>
    rule === "min_value:0"
      ? `min_value:${values.estimatedCostForSNFABN}`
      : rule,
  );

  // Each WorkflowSettingsSection fetches its own workflow-settings template,
  // so it registers a validator here instead of the parent loading them all.
  const workflowValidatorsRef = useRef<
    Record<string, WorkflowSettingsValidator>
  >({});

  const registerWorkflowValidator = useCallback(
    (
      workflowType: string,
      validator: WorkflowSettingsValidator | undefined,
    ) => {
      if (validator) {
        workflowValidatorsRef.current[workflowType] = validator;
      } else {
        delete workflowValidatorsRef.current[workflowType];
      }
    },
    [],
  );

  const validateForm = useCallback(
    values => ({
      ...validate(values, {
        estimatedMaxCostForSNFABN,
        facilityName: ["required", "facility-name"],
        facilityGoLiveDate:
          initialValues.facilityGoLiveDate === ""
            ? FACILITY_GO_LIVE_DATE_BLOCK.rules
            : [...FACILITY_GO_LIVE_DATE_BLOCK.rules, "required"], // Required only if go live date is set, to prevent empty date from being saved
      }),
      ...validateWorkflowChecklistDescriptions(values),
      ...Object.values(workflowValidatorsRef.current).reduce(
        (errors, validateWorkflow) => ({
          ...errors,
          ...validateWorkflow(values),
        }),
        {},
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validate, estimatedMaxCostForSNFABN, initialValues.facilityGoLiveDate],
  );

  const enrichedQueryVariables = {
    ...queryVariables,
    withAccessToken,
    getCommonAuthorizationHeader,
  };

  return (
    <FacilitySettingsReadOnlyProvider value={isReadOnly}>
      <EditFacilityModalProvider>
        <EditFacilityConfirmationModal />
        <ChangeTemplateModal
          isOpen={isChangeTemplateModalOpen}
          onClose={() => setIsChangeTemplateModalOpen(false)}
          templateName={template.templateName}
        />
        <Formik<FacilitySettingsVariables>
          initialValues={values}
          validate={validateForm}
          onSubmit={async (values, formik) => {
            formik.setSubmitting(true);

            const {checklist, isPccEnabled, userRole, workflows, ...rest} =
              values;

            const normalizedWorkflows: FacilitySettingsVariables["workflows"] =
              workflows
                ? Object.fromEntries(
                    Object.entries(workflows).map(
                      ([workflowType, workflowValues]): [
                        string,
                        WorkflowSettingsVariables,
                      ] => [
                        workflowType,
                        Array.isArray(workflowValues?.checklist)
                          ? {
                              ...workflowValues,
                              checklist: getChecklistWithNormalizedIds(
                                workflowValues.checklist as ReadonlyArray<FieldArrayItem>,
                              ) as any,
                            }
                          : workflowValues,
                      ],
                    ),
                  )
                : workflows;

            const savedValues = await onSubmit({
              checklist: getChecklistWithNormalizedIds(
                checklist as ReadonlyArray<FieldArrayItem>,
              ),
              ...rest,
              ...(normalizedWorkflows ? {workflows: normalizedWorkflows} : {}),
              isPccEnabled,
              userRole,
            });

            formik.setSubmitting(false);

            if (savedValues) {
              initialValues.facilityGoLiveDate = savedValues.facilityGoLiveDate;
              formik.resetForm({
                values: {...savedValues, isPccEnabled, userRole},
              });
            }
          }}
        >
          {formik => (
            <StyledForm>
              <FormikValidateAllOnSubmit />
              <FormikReinitializePcc isPccEnabled={isPccEnabled} />
              <FormikAutoSaveValues
                disableDebounce
                onChangeValues={setValues}
              />
              <ConfirmBeforeLeave
                when={formik.dirty}
                message="After leaving all of your unsaved changes will be lost."
              />
              <ScrollToError offsetTop={200} />
              {header}

              <ScrollSpyProvider>
                <PageLayout>
                  <FacilitySettingsSideNav
                    blocks={generalBlocks}
                    pccTemplateBlocks={pccTemplateBlocks}
                    templateName={template.templateName}
                    isResideAdmin={isResideAdmin}
                    isReadOnly={isReadOnly}
                    onOpenChangeTemplate={() =>
                      setIsChangeTemplateModalOpen(true)
                    }
                  />

                  <ContentPane>
                    <ScrollableContent
                      className={FACILITY_SETTINGS_SCROLL_CONTAINER_CLASS}
                    >
                      <StyledScrollableErrorArea>
                        <ReadOnlyFieldset disabled={isReadOnly}>
                          {/* fieldset firefox implementation requires wrapping children in extra div */}
                          <div>
                            <BlockComponentContextProvider
                              value={transComponents}
                            >
                              {generalBlocks.length > 0 ? (
                                <FacilitySettingsSection id={SECTION_GENERAL}>
                                  {generalBlocks.map(block => (
                                    <FacilitySettingsScrollTarget
                                      key={String(block.id)}
                                      id={String(block.id)}
                                    >
                                      <BlockField
                                        block={block}
                                        queryVariables={enrichedQueryVariables}
                                      />
                                    </FacilitySettingsScrollTarget>
                                  ))}
                                </FacilitySettingsSection>
                              ) : null}

                              {isResideAdmin && (
                                <FacilityPccMappingForm
                                  isPccEnabled={isPccEnabled}
                                  pccTemplateBlocks={pccTemplateBlocks}
                                  queryVariables={enrichedQueryVariables}
                                >
                                  {workflows.map(workflow => (
                                    <WorkflowSettingsSection
                                      key={workflow.type}
                                      workflowType={workflow.type}
                                      workflowLabel={workflow.label}
                                      facilityTemplate={template.templateName}
                                      isPccEnabled={isPccEnabled}
                                      queryVariables={enrichedQueryVariables}
                                      facilitySettingsFormik={formik}
                                      registerWorkflowValidator={
                                        registerWorkflowValidator
                                      }
                                    />
                                  ))}
                                </FacilityPccMappingForm>
                              )}

                              {!isResideAdmin &&
                                workflows.map(workflow => (
                                  <WorkflowSettingsSection
                                    key={workflow.type}
                                    workflowType={workflow.type}
                                    workflowLabel={workflow.label}
                                    facilityTemplate={template.templateName}
                                    isPccEnabled={isPccEnabled}
                                    queryVariables={enrichedQueryVariables}
                                    facilitySettingsFormik={formik}
                                    registerWorkflowValidator={
                                      registerWorkflowValidator
                                    }
                                  />
                                ))}

                              {isResideAdmin && workflows.length > 1 && (
                                <WorkflowTriggersSection
                                  templateName={template.templateName}
                                  workflowOptions={workflows}
                                />
                              )}
                            </BlockComponentContextProvider>
                          </div>
                        </ReadOnlyFieldset>
                      </StyledScrollableErrorArea>
                    </ScrollableContent>
                  </ContentPane>
                </PageLayout>
              </ScrollSpyProvider>
            </StyledForm>
          )}
        </Formik>
      </EditFacilityModalProvider>
    </FacilitySettingsReadOnlyProvider>
  );
};
